import { Box } from "@connected-repo/ui-mui/layout/Box";
import { useThemeMode } from "@connected-repo/ui-mui/theme/ThemeContext";
import type { SessionInfo } from "@frontend/contexts/UserContext";
import { userContext, useSessionInfo } from "@frontend/contexts/UserContext";
import { useWorkspace, WorkspaceProvider } from "@frontend/contexts/WorkspaceContext";
import Fade from "@mui/material/Fade";
import { useEffect } from "react";
import { Outlet, useLoaderData, useMatches } from "react-router";
import { ConfirmEmailBanner } from "./ConfirmEmailBanner";
import { TopNav } from "./TopNav";

/**
 * A route that draws its own chrome (the desk console: its top bar IS the
 * page header, and every pixel of width goes to live text) sets this handle.
 */
export interface LayoutHandle {
	bare?: boolean;
}

const isBare = (handle: unknown): boolean =>
	typeof handle === "object" && handle !== null && "bare" in handle && handle.bare === true;

export const AppLayoutContent = () => {
	const { activeWorkspace } = useWorkspace();
	const sessionInfo = useSessionInfo();
	return (
		<Fade key={activeWorkspace.id} in timeout={400}>
			<Box sx={{ height: "100%", width: "100%" }}>
				<Outlet context={sessionInfo} />
			</Box>
		</Fade>
	);
};

/**
 * AppLayout - Main layout wrapper for authenticated pages
 *
 * One `TopNav` at every width, then the page in a 1200px column with the
 * design's 48 / 32 / 20px gutters. A route with `handle: { bare: true }`
 * gets the contexts but neither the nav nor the column.
 *
 * Session data is loaded by authLoader and passed to children via Outlet context
 * Child components access it via useOutletContext<SessionInfo>()
 */
export const AppLayout = () => {
	const bare = useMatches().some((m) => isBare(m.handle));

	// Get session data from authLoader
	const sessionInfo = useLoaderData() as SessionInfo;
	const { setThemeMode } = useThemeMode();

	useEffect(() => {
		if (sessionInfo.user?.themeSetting) {
			setThemeMode(sessionInfo.user.themeSetting);
		}
	}, [sessionInfo.user?.themeSetting, setThemeMode]);

	// NOTE: A previous version listened to `visualViewport.resize` and toggled
	// `isKeyboardOpen`, then shrunk the main padding to 0. On iOS Safari, the
	// resulting re-render + layout shift while the keyboard was opening
	// caused the input to lose focus mid-animation and dismissed the
	// keyboard again. We now leave the layout static — the browser handles
	// scrolling the focused input above the keyboard on its own.

	return (
		<userContext.Provider value={sessionInfo}>
			<WorkspaceProvider sessionInfo={sessionInfo}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						minHeight: "100vh",
						bgcolor: "background.default",
					}}
				>
					{bare ? (
						<AppLayoutContent />
					) : (
						<>
							<TopNav />
							<Box
								component="main"
								sx={{
									flexGrow: 1,
									width: "100%",
									maxWidth: 1200,
									mx: "auto",
									pt: { xs: 3, md: 5 },
									pb: { xs: 5, md: 8 },
									px: { xs: 2.5, sm: 4, md: 6 },
								}}
							>
								<ConfirmEmailBanner />
								<AppLayoutContent />
							</Box>
						</>
					)}
				</Box>
			</WorkspaceProvider>
		</userContext.Provider>
	);
};
